import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function ContactModal({ open, sessionId, onDone, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finish = async (quote) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/contact`, { ...form, email: form.email || null, quote_requested: quote });
      toast.success(quote ? "Lovely — our team will reach out shortly." : "Saved. Your board is ready to share.");
      onDone(data);
    } catch {
      toast.error("Something went wrong saving your details.");
    }
    setBusy(false);
  };

  const steps = [
    {
      key: "name",
      eyebrow: "You have a lovely taste",
      title: "May I just know your name?",
      input: <input data-testid="contact-name-input" autoFocus className="input-line" placeholder="Your name" value={form.name} onChange={set("name")} />,
      valid: form.name.trim().length > 1,
    },
    {
      key: "phone",
      eyebrow: `Nice to meet you, ${form.name.split(" ")[0]}`,
      title: "And a number we can reach you on?",
      input: <input data-testid="contact-phone-input" autoFocus type="tel" className="input-line" placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} />,
      valid: form.phone.replace(/\D/g, "").length >= 8,
    },
    {
      key: "email",
      eyebrow: "Optional",
      title: "An email, so we can send your board?",
      input: <input data-testid="contact-email-input" autoFocus type="email" className="input-line" placeholder="you@example.com" value={form.email} onChange={set("email")} />,
      valid: true,
    },
  ];
  const cur = steps[step];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="contact-modal" className="max-w-lg rounded-3xl p-10 border-0">
        <AnimatePresence mode="wait">
          {step < 3 ? (
            <motion.div key={cur.key} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
              <p className="eyebrow">{cur.eyebrow}</p>
              <h3 className="font-display text-3xl sm:text-4xl font-light mt-3 leading-tight">{cur.title}</h3>
              <form className="mt-8" onSubmit={(e) => { e.preventDefault(); cur.valid && setStep(step + 1); }}>
                {cur.input}
                <div className="flex justify-between items-center mt-8">
                  <span className="text-xs text-neutral-400">{step + 1} / 3</span>
                  <button data-testid="contact-next-button" className="btn-ink" disabled={!cur.valid}>
                    {step === 2 ? (form.email ? "Continue" : "Skip") : "Next"}
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div key="quote" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
              <p className="eyebrow">One last thing</p>
              <h3 className="font-display text-3xl sm:text-4xl font-light mt-3 leading-tight">
                Would you like a quote, or someone to help you build this?
              </h3>
              <p className="text-neutral-500 mt-4 leading-relaxed">
                Drop us a yes and our team eases out the sourcing — furniture, finishes, the lot — built around this exact board.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button data-testid="quote-yes-button" disabled={busy} onClick={() => finish(true)} className="btn-ink flex-1">Yes, help me build it</button>
                <button data-testid="quote-no-button" disabled={busy} onClick={() => finish(false)} className="btn-ghost flex-1">Just the board for now</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
